import React, { useState, useEffect, useMemo } from 'react';
import Footer from '../components/Footer';
import FotoUpload from '../components/FotoUpload';
import axiosInstance from '../utils/axios';
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  Select,
  message,
  Card,
  Space,
  Tag,
  Tooltip,
} from 'antd';
import {
  CarOutlined,
  PlusOutlined,
  SearchOutlined,
  HomeOutlined,
  UserOutlined,
  BgColorsOutlined,
  CalendarOutlined,
  TagOutlined,
  WifiOutlined,
  RadarChartOutlined,
  FileProtectOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useVehicles } from '../shared/hooks/useVehicles';
import { hssColors, commonStyles } from '../config/theme';
import { isAdmin, canEdit } from '../utils/auth';

const { Option } = Select;

const Vehiculos = () => {
  const navigate = useNavigate();
  const { vehicles, loading, loadVehicles, handleCreateVehicle } = useVehicles(
    axiosInstance,
    (error) => message.error(error)
  );
  const [modalVisible, setModalVisible] = useState(false);
  const [modalEditarVisible, setModalEditarVisible] = useState(false);
  const [vehiculoEditando, setVehiculoEditando] = useState(null);
  const [form] = Form.useForm();
  const [formEditar] = Form.useForm();
  const [clientes, setClientes] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [gpsDisponibles, setGpsDisponibles] = useState([]);
  const [dvrsDisponibles, setDvrsDisponibles] = useState([]);
  const [routersDisponibles, setRoutersDisponibles] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState(null);
  const [paginaActual, setPaginaActual] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // leer ?tipo= de la URL al cargar
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('tipo');
    if (t) setTipoFiltro(t);
  }, []);

  useEffect(() => {
    cargarClientes();
    cargarContratos();
    cargarDisponibles();
  }, []);

  const cargarDisponibles = async () => {
    try {
      const [gpsR, dvrsR, routersR] = await Promise.all([
        axiosInstance.get('/gps'),
        axiosInstance.get('/vision/dvr'),
        axiosInstance.get('/vision/routers'),
      ]);
      setGpsDisponibles(gpsR.data.filter(g => !g.vehiculo_chapa));
      setDvrsDisponibles(dvrsR.data.filter(d => !d.vehiculo_chapa));
      setRoutersDisponibles(routersR.data.filter(r => !r.vehiculo_chapa));
    } catch {
      // silencioso — no bloquea el formulario
    }
  };

  const cargarContratos = async () => {
    try {
      const response = await axiosInstance.get('/contratos');
      setContratos(response.data);
    } catch {
      // silencioso — no es crítico para la página de vehículos
    }
  };

  const cargarClientes = async () => {
    try {
      const response = await axiosInstance.get('/clientes');
      setClientes(response.data);
    } catch {
      message.error('Error al cargar clientes');
    }
  };

  const onFinish = async (values) => {
    const success = await handleCreateVehicle(values);
    if (success) {
      message.success('Vehículo creado exitosamente');
      setModalVisible(false);
      form.resetFields();
    }
  };

  const abrirEditar = (vehiculo) => {
    setVehiculoEditando(vehiculo);
    formEditar.setFieldsValue({
      marca: vehiculo.marca,
      modelo: vehiculo.modelo,
      alias: vehiculo.alias || undefined,
      color: vehiculo.color,
      ano: vehiculo.ano,
      tipo: vehiculo.tipo,
      chasis: vehiculo.chasis,
      gps: vehiculo.gps,
      servicio: vehiculo.servicio,
      estado: vehiculo.estado || 'instalado',
      cliente_dni: vehiculo.cliente_dni || undefined,
    });
    setModalEditarVisible(true);
  };

  const onFinishEditar = async (values) => {
    try {
      await axiosInstance.put(`/vehiculos/${vehiculoEditando.chapa}`, values);
      message.success('Vehículo actualizado');
      setModalEditarVisible(false);
      loadVehicles();
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al actualizar vehículo');
    }
  };

  const filteredVehicles = useMemo(() => vehicles.filter(vehiculo => {
    if (tipoFiltro && vehiculo.tipo?.toLowerCase() !== tipoFiltro.toLowerCase()) return false;
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    const cliente = clientes.find(c => c.dni === vehiculo.cliente_dni);
    const nombreCliente = cliente ? `${cliente.nombre} ${cliente.apellido}`.toLowerCase() : '';
    return (
      vehiculo.chapa?.toLowerCase().includes(searchLower) ||
      vehiculo.marca?.toLowerCase().includes(searchLower) ||
      vehiculo.modelo?.toLowerCase().includes(searchLower) ||
      vehiculo.nombre_gps?.toLowerCase().includes(searchLower) ||
      vehiculo.alias?.toLowerCase().includes(searchLower) ||
      vehiculo.color?.toLowerCase().includes(searchLower) ||
      vehiculo.ano?.toString().includes(searchLower) ||
      vehiculo.tipo?.toLowerCase().includes(searchLower) ||
      vehiculo.chasis?.toLowerCase().includes(searchLower) ||
      nombreCliente.includes(searchLower) ||
      vehiculo.imei?.includes(searchLower)
    );
  }), [vehicles, searchText, tipoFiltro, clientes]);

  const tipoColors = {
    camion: '#e74c3c',
    camioneta: '#3498db',
    automovil: '#2ecc71',
    moto: '#f39c12',
    tractor: '#9b59b6',
    barco: '#1abc9c',
    avion: '#34495e',
  };

  const servicioConfig = {
    RASTREO:        { color: '#3498db', label: 'Rastreo' },
    VISION:         { color: '#e74c3c', label: 'HSS Vision' },
    RASTREO_VISION: { color: '#9b59b6', label: 'Rastreo + Vision' },
  };

  const estadoConfig = {
    instalado:    { color: 'success',   label: 'Instalado',    dot: '#52c41a' },
    desinstalado: { color: 'warning',   label: 'Desinstalado', dot: '#faad14' },
    baja:         { color: 'error',     label: 'Baja',         dot: '#ff4d4f' },
  };

  const columns = [
    {
      title: 'Chapa',
      dataIndex: 'chapa',
      key: 'chapa',
      sorter: (a, b) => (a.chapa || '').localeCompare(b.chapa || ''),
      render: (chapa) => (
        <Space
          style={{ cursor: 'pointer' }}
          onClick={() => navigate(`/vehiculos/${chapa}`)}
        >
          <TagOutlined style={{ color: hssColors.primary }} />
          <span style={{ fontWeight: '700', color: hssColors.primary, textDecoration: 'underline' }}>{chapa}</span>
        </Space>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      sorter: (a, b) => (a.estado || '').localeCompare(b.estado || ''),
      render: (estado) => {
        const cfg = estadoConfig[estado] || { color: 'default', label: estado || 'Instalado', dot: '#888' };
        return (
          <Tag
            color={cfg.color}
            style={{ fontWeight: 700, fontSize: 12, padding: '2px 10px', borderRadius: 20 }}
          >
            {cfg.label}
          </Tag>
        );
      },
    },
    {
      title: 'Marca',
      dataIndex: 'marca',
      key: 'marca',
      sorter: (a, b) => (a.marca || '').localeCompare(b.marca || ''),
      render: (marca) => (
        <span style={{ fontWeight: '500' }}>{marca}</span>
      ),
    },
    {
      title: 'Modelo / Alias',
      key: 'modelo',
      sorter: (a, b) => (a.modelo || '').localeCompare(b.modelo || ''),
      render: (_, v) => (
        <div>
          <div style={{ fontWeight: 500 }}>{v.modelo || '-'}</div>
          {v.nombre_gps && <div style={{ fontSize: 12, color: hssColors.info }}>{v.nombre_gps}</div>}
          {v.alias && <div style={{ fontSize: 12, color: hssColors.textSecondary }}>{v.alias}</div>}
        </div>
      ),
    },
    {
      title: 'Color',
      dataIndex: 'color',
      key: 'color',
      sorter: (a, b) => (a.color || '').localeCompare(b.color || ''),
      render: (color) => color ? (
        <Space>
          <BgColorsOutlined style={{ color: hssColors.info }} />
          <span>{color}</span>
        </Space>
      ) : '-',
    },
    {
      title: 'Año',
      dataIndex: 'ano',
      key: 'ano',
      sorter: (a, b) => (a.ano || 0) - (b.ano || 0),
      render: (ano) => ano ? (
        <Space>
          <CalendarOutlined style={{ color: hssColors.warning }} />
          <span>{ano}</span>
        </Space>
      ) : '-',
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      sorter: (a, b) => (a.tipo || '').localeCompare(b.tipo || ''),
      render: (tipo) => tipo ? (
        <Tooltip title={tipoFiltro === tipo ? 'Click para quitar filtro' : `Click para filtrar por ${tipo}`}>
          <Tag
            color={tipoFiltro === tipo ? 'processing' : (tipoColors[tipo.toLowerCase()] || 'default')}
            style={{ cursor: 'pointer', fontWeight: tipoFiltro === tipo ? 700 : 500 }}
            onClick={() => setTipoFiltro(tipoFiltro === tipo ? null : tipo)}
          >
            {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
            {tipoFiltro === tipo ? ' ×' : ''}
          </Tag>
        </Tooltip>
      ) : '-',
    },
    {
      title: 'Servicio',
      dataIndex: 'servicio',
      key: 'servicio',
      sorter: (a, b) => (a.servicio || '').localeCompare(b.servicio || ''),
      render: (servicio) => {
        if (!servicio) return <Tag color="default">Sin servicio</Tag>;
        const cfg = servicioConfig[servicio] || { color: 'default', label: servicio };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Cliente',
      dataIndex: 'cliente_dni',
      key: 'cliente_dni',
      sorter: (a, b) => {
        const ca = clientes.find(c => c.dni === a.cliente_dni);
        const cb = clientes.find(c => c.dni === b.cliente_dni);
        return (`${ca?.nombre} ${ca?.apellido}` || '').localeCompare(`${cb?.nombre} ${cb?.apellido}` || '');
      },
      render: (dni) => {
        const cliente = clientes.find(c => c.dni === dni);
        return cliente ? (
          <Space>
            <UserOutlined style={{ color: hssColors.success }} />
            <span>{`${cliente.nombre} ${cliente.apellido}`}</span>
          </Space>
        ) : dni;
      }
    },
    {
      title: 'Fotos',
      key: 'fotos',
      width: 280,
      render: (_, v) => (
        <Space size={12}>
          <FotoUpload
            label="Vehículo"
            urlEndpoint={`/vehiculos/${v.chapa}/fotos`}
            uploadExtra={{ tipo: 'vehiculo' }}
            fotoActual={v.foto_vehiculo}
            onSuccess={loadVehicles}
            onDelete={loadVehicles}
            deleteEndpoint={`/vehiculos/${v.chapa}/fotos/vehiculo`}
          />
          <FotoUpload
            label="Documentación"
            urlEndpoint={`/vehiculos/${v.chapa}/fotos`}
            uploadExtra={{ tipo: 'cedula_verde' }}
            fotoActual={v.foto_cedula_verde}
            onSuccess={loadVehicles}
            onDelete={loadVehicles}
            deleteEndpoint={`/vehiculos/${v.chapa}/fotos/cedula_verde`}
          />
        </Space>
      ),
    },
    {
      title: 'Contrato',
      key: 'contrato',
      render: (_, vehiculo) => {
        // Busca contratos donde este vehículo aparezca en la lista de vehículos
        const contrato = contratos.find(c =>
          c.vehiculos?.some(v => v.chapa === vehiculo.chapa) || c.vehiculo_chapa === vehiculo.chapa
        );
        if (!contrato) return <Tag color="default" style={{ fontSize: 11 }}>Sin contrato</Tag>;
        return (
          <Space direction="vertical" size={0}>
            <Tag color={contrato.estado === 'FIRMADO' ? 'success' : 'warning'} style={{ fontSize: 11 }}>
              {contrato.estado === 'FIRMADO' ? '✓ Firmado' : '⏳ Pendiente'}
            </Tag>
            <span style={{ fontSize: 10, color: hssColors.textSecondary, fontFamily: 'monospace' }}>
              #{contrato.id?.slice(0, 8).toUpperCase()}
            </span>
          </Space>
        );
      }
    },
    ...(canEdit() ? [{
      title: '',
      key: 'acciones',
      width: 60,
      render: (_, record) => (
        <Tooltip title="Editar vehículo">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={e => { e.stopPropagation(); abrirEditar(record); }}
            style={{ color: hssColors.primary }}
          />
        </Tooltip>
      ),
    }] : []),
  ];

  const tiposVehiculo = [
    'camion', 'camioneta', 'automovil', 'moto',
    'tractor', 'acoplado', 'semirremolque', 'barco', 'avion'
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: hssColors.backgroundPrimary,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{ background: hssColors.backgroundSecondary, borderBottom: `1px solid ${hssColors.gray200}`, padding: '16px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CarOutlined style={{ fontSize: 28, color: hssColors.primary }} />
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: hssColors.primary }}>Gestión de Vehículos</h1>
              <p style={{ margin: 0, fontSize: 13, color: hssColors.textSecondary }}>Administración de vehículos del sistema</p>
            </div>
          </div>
          <Space>
            <Button icon={<HomeOutlined />} onClick={() => navigate('/dashboard')} size="large" style={{ height: 44, borderRadius: 8 }}>Inicio</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)} size="large" style={{ height: 44, borderRadius: 8, fontWeight: 600 }}>
              Nuevo Vehículo
            </Button>
          </Space>
        </div>
      </div>

      {/* Contenido principal */}
      <div style={{ padding: '24px 32px', flex: 1 }}>
        {/* Stats */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Total vehículos', value: vehicles.length, icon: <CarOutlined />, color: hssColors.primary },
            { label: 'Instalados', value: vehicles.filter(v => !v.estado || v.estado === 'instalado').length, icon: <CarOutlined />, color: hssColors.success },
            { label: 'Desinstalados', value: vehicles.filter(v => v.estado === 'desinstalado').length, icon: <CarOutlined />, color: '#e67e22' },
            { label: 'Dados de baja', value: vehicles.filter(v => v.estado === 'baja').length, icon: <CarOutlined />, color: hssColors.error },
          ].map(s => (
            <Card key={s.label} loading={loading} style={{ flex: 1, borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 28, color: s.color }}>{s.icon}</div>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: hssColors.textSecondary }}>{s.label}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Búsqueda */}
        <Card style={{ marginBottom: '24px', borderRadius: '12px', border: 'none', boxShadow: commonStyles.shadowMd }} styles={{ body: { padding: '20px 24px' } }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Input
              placeholder="Buscar vehículo por chapa, marca, modelo, color, año, chasis..."
              prefix={<SearchOutlined style={{ color: hssColors.gray400 }} />}
              style={{ flex: 1, minWidth: 300, borderRadius: '8px' }}
              size="large"
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setPaginaActual(1); }}
              allowClear
            />
            {tipoFiltro && (
              <Tag closable onClose={() => setTipoFiltro(null)} color="processing" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20, fontWeight: 600 }}>
                Tipo: {tipoFiltro}
              </Tag>
            )}
          </div>
        </Card>

        {/* Tabla de vehículos */}
        <Card
          style={{
            borderRadius: '12px',
            border: 'none',
            boxShadow: commonStyles.shadowMd,
          }}
          styles={{
            body: { padding: 0 }
          }}
        >
          <Table
            columns={columns}
            dataSource={filteredVehicles}
            rowKey="chapa"
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: paginaActual,
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (p, size) => { setPaginaActual(p); setPageSize(size); },
              showTotal: (total) => `Total: ${total} vehículos`,
            }}
            style={{
              borderRadius: '12px',
              overflow: 'hidden',
            }}
          />
        </Card>
      </div>

      {/* Modal Nuevo Vehículo */}
      <Modal
        title={
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '20px',
            fontWeight: '700',
            color: hssColors.primary,
          }}>
            <CarOutlined />
            <span>Nuevo Vehículo</span>
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={form} onFinish={onFinish} layout="vertical" style={{ marginTop: '24px' }}>
          <Form.Item
            name="chapa"
            label="Chapa / Patente"
            rules={[{ required: true, message: 'Ingrese la chapa' }]}
          >
            <Input
              prefix={<TagOutlined style={{ color: hssColors.gray400 }} />}
              placeholder="Ej: ABC-123"
              size="large"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item
            name="marca"
            label="Marca"
            rules={[{ required: true, message: 'Ingrese la marca' }]}
          >
            <Input
              prefix={<CarOutlined style={{ color: hssColors.gray400 }} />}
              placeholder="Ej: Toyota, Ford, Honda..."
              size="large"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item name="modelo" label="Modelo">
            <Input
              placeholder="Ej: Corolla, F-150, Civic..."
              size="large"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item name="alias" label="Alias / Subnombre">
            <Input
              placeholder="Ej: Camión del norte, Flota 3..."
              size="large"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item name="color" label="Color">
            <Input
              prefix={<BgColorsOutlined style={{ color: hssColors.gray400 }} />}
              placeholder="Ej: Rojo, Azul, Negro..."
              size="large"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item name="ano" label="Año">
            <Input
              prefix={<CalendarOutlined style={{ color: hssColors.gray400 }} />}
              placeholder="Ej: 2023"
              type="number"
              size="large"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item
            name="cliente_dni"
            label="Cliente Propietario"
          >
            <Select
              placeholder="Sin cliente asignado (opcional)"
              allowClear
              size="large"
              style={{ borderRadius: '8px' }}
              showSearch
              optionFilterProp="label"
            >
              {clientes.map(cliente => (
                <Option key={cliente.dni} value={cliente.dni} label={`${cliente.nombre} ${cliente.apellido} DNI ${cliente.dni}`}>
                  {cliente.nombre} {cliente.apellido} - DNI: {cliente.dni}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="tipo" label="Tipo de Vehículo">
            <Select placeholder="Seleccione el tipo" size="large" style={{ borderRadius: '8px' }}>
              {tiposVehiculo.map(tipo => (
                <Option key={tipo} value={tipo}>
                  <Tag color={tipoColors[tipo]}>{tipo.charAt(0).toUpperCase() + tipo.slice(1)}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="servicio" label="Servicio contratado">
            <Select placeholder="Seleccione el servicio" size="large" style={{ borderRadius: '8px' }} allowClear>
              <Option value="RASTREO"><Tag color="#3498db">Rastreo Satelital</Tag></Option>
              <Option value="VISION"><Tag color="#e74c3c">HSS Vision (Cámaras)</Tag></Option>
              <Option value="RASTREO_VISION"><Tag color="#9b59b6">Rastreo + HSS Vision</Tag></Option>
            </Select>
          </Form.Item>

          <Form.Item name="estado" label="Estado" initialValue="instalado">
            <Select size="large" style={{ borderRadius: '8px' }}>
              <Option value="instalado"><Tag color="success">Instalado</Tag></Option>
              <Option value="desinstalado"><Tag color="warning">Desinstalado</Tag></Option>
              <Option value="baja"><Tag color="error">Baja</Tag></Option>
            </Select>
          </Form.Item>

          <Form.Item name="chasis" label="N° de Chasis">
            <Input placeholder="Número de chasis (opcional)" size="large" style={{ borderRadius: '8px' }} />
          </Form.Item>

          <Form.Item name="gps_id" label="GPS (dispositivo en stock)">
            <Select placeholder="Sin GPS asignado (opcional)" allowClear size="large" style={{ borderRadius: '8px' }} showSearch optionFilterProp="label">
              {gpsDisponibles.map(g => (
                <Option key={g.id} value={g.id} label={`${g.marca} ${g.modelo} ${g.imei}`}>
                  <WifiOutlined style={{ color: '#3498db', marginRight: 6 }} />
                  {g.marca} {g.modelo} — <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{g.imei}</span>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="dvr_id" label="DVR (en stock)">
            <Select placeholder="Sin DVR asignado (opcional)" allowClear size="large" style={{ borderRadius: '8px' }} showSearch optionFilterProp="label">
              {dvrsDisponibles.map(d => (
                <Option key={d.id} value={d.id} label={`${d.marca} ${d.modelo} ${d.numero_serie}`}>
                  <RadarChartOutlined style={{ color: '#e74c3c', marginRight: 6 }} />
                  {d.marca} {d.modelo} — <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{d.numero_serie}</span>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="router_id" label="Router (en stock)">
            <Select placeholder="Sin Router asignado (opcional)" allowClear size="large" style={{ borderRadius: '8px' }} showSearch optionFilterProp="label">
              {routersDisponibles.map(r => (
                <Option key={r.id} value={r.id} label={`${r.marca} ${r.modelo} ${r.numero_serie}`}>
                  <FileProtectOutlined style={{ color: '#27ae60', marginRight: 6 }} />
                  {r.marca} {r.modelo} — <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.numero_serie}</span>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => setModalVisible(false)}
                size="large"
                style={{ borderRadius: '8px' }}
              >
                Cancelar
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                style={{
                  borderRadius: '8px',
                  fontWeight: '600',
                  boxShadow: commonStyles.shadowMd,
                }}
              >
                Guardar Vehículo
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Editar Vehículo */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 20, fontWeight: 700, color: hssColors.primary }}>
            <EditOutlined />
            <span>Editar Vehículo — {vehiculoEditando?.chapa}</span>
          </div>
        }
        open={modalEditarVisible}
        onCancel={() => setModalEditarVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={formEditar} onFinish={onFinishEditar} layout="vertical" style={{ marginTop: 24 }}>
          <Form.Item name="marca" label="Marca" rules={[{ required: true, message: 'Ingrese la marca' }]}>
            <Input prefix={<CarOutlined style={{ color: hssColors.gray400 }} />} size="large" style={{ borderRadius: 8 }} />
          </Form.Item>
          <Form.Item name="modelo" label="Modelo">
            <Input size="large" style={{ borderRadius: 8 }} placeholder="Ej: Hilux, Gol, CG 150..." />
          </Form.Item>
          <Form.Item name="alias" label="Alias / Subnombre">
            <Input size="large" style={{ borderRadius: 8 }} placeholder="Ej: Camión del norte, Flota 3..." />
          </Form.Item>
          <Form.Item name="color" label="Color">
            <Input prefix={<BgColorsOutlined style={{ color: hssColors.gray400 }} />} size="large" style={{ borderRadius: 8 }} placeholder="Ej: Blanco, Negro, Rojo..." />
          </Form.Item>
          <Form.Item name="ano" label="Año">
            <Input prefix={<CalendarOutlined style={{ color: hssColors.gray400 }} />} size="large" style={{ borderRadius: 8 }} placeholder="Ej: 2020" />
          </Form.Item>
          <Form.Item name="tipo" label="Tipo de vehículo">
            <Select placeholder="Seleccione el tipo" size="large" style={{ borderRadius: 8 }} allowClear>
              {tiposVehiculo.map(t => <Option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="servicio" label="Servicio contratado">
            <Select placeholder="Seleccione el servicio" size="large" style={{ borderRadius: 8 }} allowClear>
              <Option value="RASTREO">Rastreo Satelital</Option>
              <Option value="VISION">HSS Vision (CCTV)</Option>
              <Option value="RASTREO_VISION">Rastreo + Vision</Option>
            </Select>
          </Form.Item>
          <Form.Item name="estado" label="Estado">
            <Select size="large" style={{ borderRadius: 8 }}>
              <Option value="instalado"><Tag color="success">Instalado</Tag></Option>
              <Option value="desinstalado"><Tag color="warning">Desinstalado</Tag></Option>
              <Option value="baja"><Tag color="error">Baja</Tag></Option>
            </Select>
          </Form.Item>
          <Form.Item name="chasis" label="Número de chasis / VIN">
            <Input size="large" style={{ borderRadius: 8 }} placeholder="Ej: 9BWZZZ377VT004251" />
          </Form.Item>
          <Form.Item name="gps" label="GPS instalado">
            <Input prefix={<WifiOutlined style={{ color: hssColors.gray400 }} />} size="large" style={{ borderRadius: 8 }} placeholder="Modelo/serie del GPS" />
          </Form.Item>
          <Form.Item name="cliente_dni" label="Cliente propietario">
            <Select
              placeholder="Sin cliente asignado (o reasignar)"
              allowClear size="large" style={{ borderRadius: 8 }}
              showSearch optionFilterProp="label"
            >
              {clientes.map(c => (
                <Option key={c.dni} value={c.dni} label={`${c.nombre} ${c.apellido || ''} ${c.dni}`}>
                  <UserOutlined style={{ marginRight: 6, color: hssColors.success }} />
                  {c.nombre} {c.apellido || ''} — {c.dni}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setModalEditarVisible(false)} size="large" style={{ borderRadius: 8 }}>Cancelar</Button>
              <Button type="primary" htmlType="submit" size="large" style={{ borderRadius: 8, fontWeight: 600 }}>
                Guardar cambios
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Footer />
    </div>
  );
};

export default Vehiculos;